import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  setOnSave: (fn: (() => Promise<void>) | null) => void;
  handleNavigation: (to: string) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null);

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider');
  }
  return context;
};

// Safe hook that returns default values if not in provider
export const useUnsavedChangesSafe = () => {
  const context = useContext(UnsavedChangesContext);
  return context || {
    hasUnsavedChanges: false,
    setHasUnsavedChanges: () => {},
    setOnSave: () => {},
    handleNavigation: () => {},
  };
};

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export const UnsavedChangesProvider = ({ children }: UnsavedChangesProviderProps) => {
  const navigate = useNavigate();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  
  // Use ref for onSave to avoid re-renders when it changes
  const onSaveRef = useRef<(() => Promise<void>) | null>(null);
  
  // Stable setter that doesn't cause re-renders
  const setOnSave = useCallback((fn: (() => Promise<void>) | null) => {
    onSaveRef.current = fn;
  }, []);

  const handleNavigation = useCallback((to: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(to);
      setShowDialog(true);
    } else {
      navigate(to);
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmNavigation = () => {
    setShowDialog(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const cancelNavigation = () => {
    setShowDialog(false);
    setPendingNavigation(null);
  };

  const saveAndNavigate = async () => {
    try {
      if (onSaveRef.current) {
        await onSaveRef.current();
      }
      setShowDialog(false);
      setHasUnsavedChanges(false);
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch {
      setShowDialog(false);
    }
  };

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setOnSave,
        handleNavigation,
      }}
    >
      {children}

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent className="sm:max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non sauvegardées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non sauvegardées. Que souhaitez-vous faire ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <AlertDialogCancel 
              onClick={cancelNavigation}
              className="sm:mr-auto"
            >
              Annuler
            </AlertDialogCancel>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                variant="destructive" 
                onClick={confirmNavigation}
              >
                Quitter sans sauvegarder
              </Button>
              <AlertDialogAction onClick={saveAndNavigate}>
                Enregistrer et quitter
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  );
};
